import * as React from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { MobileStickyActionBar } from "@/components/ui/mobile-sticky-action-bar"
import type { PageProps } from "@/types/inertia"

type UserDetail = {
  id: number
  name: string | null
  email: string
  admin: boolean
  timezone: string | null
  created_at: string
  updated_at: string
}

type Props = {
  user: UserDetail
  timezones: string[]
}

export default function AdminUserEdit({ user, timezones }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const currentUser = props.current_user
  const isSelf = currentUser?.id === user.id

  const form = useForm({
    name: user.name ?? "",
    email: user.email ?? "",
    password: "",
    timezone: user.timezone ?? "",
    admin: user.admin ?? false,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.patch(`/admin/users/${user.id}`, {
      preserveScroll: true,
    })
  }

  const alert = props.flash?.alert

  return (
    <>
      <Head title={`Edit User: ${user.email}`}>
        <meta name="description" content={`Edit user details for ${user.email}.`} />
        <meta property="og:title" content={`Edit User: ${user.email}`} />
        <meta property="og:description" content={`Edit user details for ${user.email}.`} />
      </Head>
      <AppShell>
        <PageHeader
          title={`Edit User: ${user.name || user.email}`}
          description={`Update account details for user #${user.id}.`}
          backHref={`/admin/users/${user.id}`}
          backLabel="Back to user detail"
        />

        {alert && <p className="mt-4 text-sm text-danger-display">{alert}</p>}

        <section className="mt-6 max-w-md">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name">Full Name</label>
              <Input
                id="name"
                type="text"
                autoComplete="name"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-xs text-danger-display">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="email">Email Address</label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={form.data.email}
                onChange={(e) => form.setData("email", e.target.value)}
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-xs text-danger-display">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="password">Password</label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={form.data.password}
                onChange={(e) => form.setData("password", e.target.value)}
                aria-invalid={!!errors.password}
              />
              <p className="text-xs text-ink-muted">
                Leave blank to keep the current password.
              </p>
              {errors.password && (
                <p className="text-xs text-danger-display">{errors.password}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="timezone">Timezone</label>
              <Select
                id="timezone"
                value={form.data.timezone}
                onChange={(e) => form.setData("timezone", e.target.value)}
                aria-invalid={!!errors.timezone}
              >
                <option value="">Select a timezone...</option>
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </Select>
              {errors.timezone && (
                <p className="text-xs text-danger-display">{errors.timezone}</p>
              )}
            </div>

            <div className="pt-2">
              <label className="flex items-center gap-3 font-normal text-ink-body">
                <Checkbox
                  id="admin"
                  checked={form.data.admin}
                  disabled={isSelf}
                  onChange={(e) => form.setData("admin", e.target.checked)}
                />
                <span>Administrator</span>
              </label>
              {isSelf && (
                <p className="mt-1 text-xs text-ink-muted">
                  You cannot revoke your own administrator status.
                </p>
              )}
              {errors.admin && (
                <p className="mt-1 text-xs text-danger-display">{errors.admin}</p>
              )}
            </div>

            <MobileStickyActionBar>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? "Saving..." : "Save changes"}
              </Button>
            </MobileStickyActionBar>
          </form>
        </section>
      </AppShell>
    </>
  )
}
